import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { ref, update, get } from "firebase/database";
import { User, Phone, Mail, MapPin, CreditCard, Landmark, BookOpen, Award, Save, Clock } from "lucide-react";

export default function ApplicantForm() {
	const [formData, setFormData] = useState({
		lastName: "",
		firstName: "",
		fatherName: "",
		contactNumber: "",
		registrationNumber: "",
		email: "",
		birthDate: "",
		birthPlace: "",
		taluka: "",
		trade: "",
		qualification: "",
		aadhaar: "",
		pan: "",
		gst: "",
		institute: "",
		passingYear: "",
		workplace: "",
		organizationName: "",
		bankName: "",
		bankAddress: "",
		address: "",
	});

	const [loading, setLoading] = useState(false);
	const [userInfo, setUserInfo] = useState(null);
	const [isEditing, setIsEditing] = useState(false);
	const [originalData, setOriginalData] = useState({});

	useEffect(() => {
		const currentUser = JSON.parse(localStorage.getItem('currentUser'));
		if (currentUser) {
			setUserInfo(currentUser);
			
			// Fetch fresh data from Firebase
			const fetchUserData = async () => {
				try {
					const userRef = ref(db, `${currentUser._node}/${currentUser._key}`);
					const snapshot = await get(userRef);
					if (snapshot.exists()) {
						const dbData = snapshot.val();
						const userData = {
							firstName: dbData.firstName || "",
							lastName: dbData.surname || dbData.lastName || "",
							fatherName: dbData.middleName || dbData.chairmanName || "",
							contactNumber: dbData.whatsappNo || dbData.chairmanWhatsapp || "",
							registrationNumber: dbData.registrationNo || dbData.registrationNumber || "",
							email: dbData.email || "",
							aadhaar: dbData.aadharNo || dbData.chairmanAadhar || "",
							pan: dbData.panNo || dbData.societyPan || "",
							gst: dbData.gstNo || dbData.societyGst || "",
							birthDate: dbData.dob || dbData.registrationDate || "",
							birthPlace: dbData.birthPlace || "",
							taluka: dbData.taluka || "",
							trade: dbData.trade || "",
							qualification: dbData.technicalQualification || "",
							organizationName: dbData.organizationName || dbData.societyName || "",
							institute: dbData.universityName || "",
							passingYear: dbData.passingYear || "",
							workplace: dbData.businessLocation || "",
							bankName: dbData.bankName || "",
							bankAddress: dbData.bankAddress || "",
							address: dbData.currentAddress || dbData.chairmanAddress || dbData.societyAddress || "",
						};
						setFormData(userData);
						setOriginalData(userData);
					}
				} catch (error) {
					console.error("Error fetching user data:", error);
				}
			};
			fetchUserData();
		}
	}, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!userInfo || !userInfo._key || !userInfo._node) {
			alert("वापरकर्ता माहिती सापडली नाही. कृपया पुन्हा लॉगिन करा.");
			return;
		}

		setLoading(true);
		try {
			const isSociety = userInfo._node.includes('Society');
			let updateData = {};

			if (isSociety) {
				updateData = {
					societyName: formData.organizationName,
					societyAddress: formData.address,
					registrationNo: formData.registrationNumber,
					registrationDate: formData.birthDate,
					taluka: formData.taluka,
					chairmanName: formData.fatherName,
					chairmanWhatsapp: formData.contactNumber,
					chairmanAadhar: formData.aadhaar,
					societyPan: formData.pan,
					societyGst: formData.gst,
					chairmanAddress: formData.bankAddress || formData.address,
					email: formData.email,
				};
			} else {
				updateData = {
					surname: formData.lastName,
					firstName: formData.firstName,
					middleName: formData.fatherName,
					whatsappNo: formData.contactNumber,
					registrationNo: formData.registrationNumber,
					email: formData.email,
					dob: formData.birthDate,
					birthPlace: formData.birthPlace,
					taluka: formData.taluka,
					aadharNo: formData.aadhaar,
					panNo: formData.pan,
					gstNo: formData.gst,
					technicalQualification: formData.qualification,
					trade: formData.trade,
					organizationName: formData.organizationName,
					universityName: formData.institute,
					passingYear: formData.passingYear,
					businessLocation: formData.workplace,
					bankName: formData.bankName,
					bankAddress: formData.bankAddress,
					currentAddress: formData.address,
				};
			}

			const userRef = ref(db, `${userInfo._node}/${userInfo._key}`);
			await update(userRef, updateData);

			const updatedUser = { ...userInfo, ...updateData };
			localStorage.setItem('currentUser', JSON.stringify(updatedUser));
			setUserInfo(updatedUser);

			alert("माहिती यशस्वीपणे अद्यतनित केली!");
			setIsEditing(false);
			setOriginalData(formData);
		} catch (error) {
			console.error("Update failed:", error);
			alert("माहिती जतन करताना त्रुटी आली.");
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		setFormData(originalData);
		setIsEditing(false);
	};

	return (
		<div className="pb-8">
			{/* Page Header */}
			<div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-2">
				<div>
					<h1 className="text-2xl font-bold text-slate-800">अर्जदाराची संपूर्ण माहिती</h1>
					<p className="text-sm text-slate-500 mt-1">येथे आपली वैयक्तिक आणि व्यावसायिक माहिती अपडेट करा</p>
				</div>
				<nav className="text-sm text-slate-400 bg-white px-4 py-2 rounded-full shadow-sm border">
					सांगली जिल्हा परिषद / <span className="text-slate-700 font-medium font-inter">अर्जदाराची माहिती</span>
				</nav>
			</div>

			<div className="max-w-7xl mx-auto">
				<form onSubmit={handleSubmit} className="space-y-8 pb-12">
					
					{/* Section 1: Personal Details */}
					<div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
						<div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center gap-2">
							<User size={20} className="text-blue-600" />
							<h3 className="font-bold text-slate-700 uppercase tracking-wider">वैयक्तिक माहिती (Personal Details)</h3>
						</div>
						<div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
							<Input icon={<User size={16}/>} label="अर्जदार आडनाव" name="lastName" value={formData.lastName} onChange={handleChange} required disabled={!isEditing} />
							<Input icon={<User size={16}/>} label="पहिले नाव" name="firstName" value={formData.firstName} onChange={handleChange} required disabled={!isEditing} />
							<Input icon={<User size={16}/>} label="वडिलांचे नाव / पतीचे नाव" name="fatherName" value={formData.fatherName} onChange={handleChange} required disabled={!isEditing} />
							
							<Input icon={<Phone size={16}/>} label="कॉंटॅक्ट नंबर" name="contactNumber" value={formData.contactNumber} onChange={handleChange} required disabled={!isEditing} />
							<Input icon={<BookOpen size={16}/>} label="नोंदणी क्रमांक" name="registrationNumber" value={formData.registrationNumber} onChange={handleChange} required disabled={!isEditing} />
							<Input icon={<Mail size={16}/>} label="ई-मेल आयडी" name="email" type="email" value={formData.email} onChange={handleChange} required disabled={!isEditing} />
							
							<Input label="जन्मतारीख" name="birthDate" type="date" value={formData.birthDate} onChange={handleChange} disabled={!isEditing} />
							<Input label="जन्म स्थान" name="birthPlace" value={formData.birthPlace} onChange={handleChange} disabled={!isEditing} />
							<Select
								label="तालुका"
								name="taluka"
								value={formData.taluka}
								onChange={handleChange}
								required
								disabled={!isEditing}
								options={[
									{ value: "Shirala", label: "शिराळा" },
									{ value: "Walwa", label: "वाळवा" },
									{ value: "Tasgaon", label: "तासगांव" },
									{ value: "Khanapur", label: "खानापूर (विटा)" },
									{ value: "Atpadi", label: "आटपाडी" },
									{ value: "Kavathe Mahankal", label: "कवठे महांकाळ" },
									{ value: "Miraj", label: "मिरज" },
									{ value: "Palus", label: "पलूस" },
									{ value: "Jat", label: "जत" },
									{ value: "Kadegaon", label: "कडेगांव" }
								]}
							/>
						</div>
					</div>

					{/* Section 2: Technical & Business */}
					<div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
						<div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center gap-2">
							<Award size={20} className="text-blue-600" />
							<h3 className="font-bold text-slate-700 uppercase tracking-wider">तांत्रिक आणि व्यवसायाची माहिती (Technical & Business)</h3>
						</div>
						<div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
							<Select
								label="ट्रेड"
								name="trade"
								value={formData.trade}
								onChange={handleChange}
								required
								disabled={!isEditing}
								options={[
									{ value: "Civil", label: "सिव्हिल" },
									{ value: "Mechanical", label: "मेकॅनिकल" },
									{ value: "Electrical", label: "इलेक्ट्रिकल" },
									{ value: "Contractor", label: "कॉन्ट्रॅक्टर" },
									{ value: "Furniture Work", label: "फर्निचर वर्क" }
								]}
							/>
							<Select
								label="तांत्रिक पात्रता"
								name="qualification"
								value={formData.qualification}
								onChange={handleChange}
								required
								disabled={!isEditing}
								options={[
									{ value: "Diploma", label: "डिप्लोमा (Diploma)" },
									{ value: "Degree", label: "पदवी (Degree)" },
									{ value: "Construction Supervisor", label: "कन्स्ट्रक्शन सुपरव्हायझर" },
									{ value: "Building Maintenance", label: "बिल्डिंग मेंटेनन्स" }
								]}
							/>
							<Input icon={<CreditCard size={16}/>} label="आधार क्रमांक" name="aadhaar" value={formData.aadhaar} onChange={handleChange} required disabled={!isEditing} />
							
							<Input icon={<CreditCard size={16}/>} label="पॅन क्रमांक" name="pan" value={formData.pan} onChange={handleChange} required disabled={!isEditing} />
							<Input icon={<CreditCard size={16}/>} label="जीएसटी क्रमांक" name="gst" value={formData.gst} onChange={handleChange} required disabled={!isEditing} />
							<Input icon={<Landmark size={16}/>} label="संस्थेचे नाव" name="organizationName" value={formData.organizationName} onChange={handleChange} disabled={!isEditing} />
							
							<Input label="विद्यापीठाचे नाव" name="institute" value={formData.institute} onChange={handleChange} disabled={!isEditing} />
							<Input label="उत्तीर्ण वर्ष" name="passingYear" value={formData.passingYear} onChange={handleChange} disabled={!isEditing} />
							<Input icon={<MapPin size={16}/>} label="व्यवसायाचे ठिकाण" name="workplace" value={formData.workplace} onChange={handleChange} required disabled={!isEditing} />
						</div>
					</div>

					{/* Section 3: Bank & Address */}
					<div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
						<div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex items-center gap-2">
							<Landmark size={20} className="text-blue-600" />
							<h3 className="font-bold text-slate-700 uppercase tracking-wider">बँक आणि पत्ता तपशील (Bank & Address)</h3>
						</div>
						<div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
							<Input icon={<Landmark size={16}/>} label="बँकेचे नाव" name="bankName" value={formData.bankName} onChange={handleChange} disabled={!isEditing} />
							<div className="md:col-span-2">
								<Input icon={<MapPin size={16}/>} label="सध्याचा पत्ता" name="address" value={formData.address} onChange={handleChange} disabled={!isEditing} />
							</div>
							<div className="md:col-span-3">
								<Input icon={<MapPin size={16}/>} label="बँकेचा पत्ता" name="bankAddress" value={formData.bankAddress} onChange={handleChange} disabled={!isEditing} />
							</div>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="flex justify-end gap-4">
						{!isEditing ? (
							<button
								type="button"
								onClick={() => setIsEditing(true)}
								className="flex items-center gap-2 px-10 py-3.5 rounded-lg font-bold text-white shadow-lg bg-blue-600 hover:bg-blue-700 shadow-blue-200 transition-all active:scale-[0.98]"
							>
								<Save size={20} /> माहिती बदल करा (Edit Information)
							</button>
						) : (
							<>
								<button
									type="button"
									onClick={handleCancel}
									disabled={loading}
									className="flex items-center gap-2 px-10 py-3.5 rounded-lg font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-all active:scale-[0.98]"
								>
									रद्द करा (Cancel)
								</button>
								<button
									type="submit"
									disabled={loading}
									className={`flex items-center gap-2 px-10 py-3.5 rounded-lg font-bold text-white shadow-lg transition-all active:scale-[0.98] ${loading ? 'bg-slate-400' : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-200'}`}
								>
									{loading ? (
										<><Clock className="animate-spin" size={20} /> प्रक्रिया सुरू आहे...</>
									) : (
										<><Save size={20} /> माहिती अपडेट करा (Update Information)</>
									)}
								</button>
							</>
						)}
					</div>

				</form>
			</div>
		</div>
	);
}

function Input({ label, name, value, onChange, required, type = "text", icon, disabled }) {
	return (
		<div className="space-y-1.5">
			<label className="flex items-center gap-2 text-sm font-bold text-slate-700">
				{icon && <span className="text-slate-400">{icon}</span>}
				{label} {required && <span className="text-red-500">*</span>}
			</label>
			<input
				type={type}
				name={name}
				value={value}
				onChange={onChange}
				required={required}
				disabled={disabled}
				className={`w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-inter text-slate-700 placeholder-slate-300 ${disabled ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : 'bg-white'}`}
			/>
		</div>
	);
}

function Select({ label, name, value, onChange, required, options = [], disabled }) {
	return (
		<div className="space-y-1.5">
			<label className="block text-sm font-bold text-slate-700">
				{label} {required && <span className="text-red-500">*</span>}
			</label>
			<select
				name={name}
				value={value}
				onChange={onChange}
				required={required}
				disabled={disabled}
				className={`w-full border border-slate-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-inter text-slate-700 ${disabled ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : 'bg-white'}`}
			>
				<option value="">{label} निवडा</option>
				{options.map((opt) => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</select>
		</div>
	);
}
